import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import chatService from '../../services/chatService';
import socketService from '../../services/socketService';
import AdminChatWindow from '../../components/Chat/AdminChatWindow';
import { FiMessageSquare, FiUsers, FiPlus, FiSearch, FiArrowLeft } from 'react-icons/fi';
import './AdminChat.css';

const AdminChat = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentAdmin, setCurrentAdmin] = useState(null);

  useEffect(() => {
    // Get current admin from localStorage
    const adminData = localStorage.getItem('adminUser');
    if (adminData && adminData !== 'undefined') {
      try {
        const admin = JSON.parse(adminData);
        setCurrentAdmin(admin);
      } catch (error) {
        console.error('Error parsing admin user data:', error);
      }
    }
    
    loadConversations();
    
    // Connect to socket
    const token = localStorage.getItem('adminToken');
    if (token) {
      socketService.connect(token);
      
      // Listen for new messages
      socketService.onNewMessage(handleNewMessage);
    }

    return () => {
      socketService.removeListener('new_message');
    };
  }, []);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const data = await chatService.getConversations();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewMessage = (message) => {
    // Refresh conversations to update last message
    loadConversations();
  };

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    
    // Join the conversation room
    socketService.joinConversation(conversation.id);
  };

  const handleCloseChat = () => {
    if (selectedConversation) {
      socketService.leaveConversation(selectedConversation.id);
    }
    setSelectedConversation(null);
  };

  const filteredConversations = conversations.filter(conv =>
    conv.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.participants?.some(p => 
      p.user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.user?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.vendor?.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getConversationPreview = (conversation) => {
    // Get participant names
    const participants = conversation.participants
      ?.filter(p => p.admin_user_id !== currentAdmin?.id)
      .map(p => {
        if (p.user) return `${p.user.first_name} ${p.user.last_name}`;
        if (p.vendor) return p.vendor.company_name;
        return 'Unknown';
      })
      .join(', ');
    
    return participants || 'No participants';
  };

  if (loading) {
    return (
      <div className="admin-chat-page-loading">
        <div className="spinner"></div>
        <p>Loading conversations...</p>
      </div>
    );
  }

  return (
    <div className="admin-chat-page">
      <div className="admin-chat-header">
        <button className="back-btn" onClick={() => navigate('/admin/dashboard')}>
          <FiArrowLeft />
        </button>
        <h1>
          <FiMessageSquare /> Admin Chat
        </h1>
      </div>

      <div className="admin-chat-container">
        {/* Conversations List */}
        <div className={`admin-conversations-panel ${selectedConversation ? 'hidden-mobile' : ''}`}>
          <div className="admin-conversations-header">
            <h2>
              <FiMessageSquare /> Messages
            </h2>
            <button className="btn-new-chat" title="New conversation">
              <FiPlus />
            </button>
          </div>

          <div className="admin-conversations-search">
            <FiSearch />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="admin-conversations-list">
            {filteredConversations.length === 0 ? (
              <div className="no-conversations">
                <FiMessageSquare size={48} />
                <p>No conversations yet</p>
                <small>Conversations with users and vendors will appear here</small>
              </div>
            ) : (
              filteredConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`admin-conversation-item ${selectedConversation?.id === conversation.id ? 'active' : ''} ${conversation.unread_count > 0 ? 'has-unread' : ''}`}
                  onClick={() => handleSelectConversation(conversation)}
                >
                  <div className="conversation-avatar">
                    {conversation.type === 'vendor_team' ? (
                      <FiUsers />
                    ) : (
                      <FiMessageSquare />
                    )}
                  </div>
                  <div className="conversation-info">
                    <div className="conversation-title">
                      {conversation.title || 'Untitled Conversation'}
                    </div>
                    <div className="conversation-preview">
                      {getConversationPreview(conversation)}
                    </div>
                  </div>
                  <div className="conversation-meta">
                    <span className="conversation-time">
                      {formatLastMessageTime(conversation.last_message_at)}
                    </span>
                    {conversation.unread_count > 0 && (
                      <span className="unread-badge">{conversation.unread_count}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Chat Window */}
        <div className={`admin-chat-panel ${!selectedConversation ? 'hidden-mobile' : ''}`}>
          {selectedConversation ? (
            <AdminChatWindow
              conversation={selectedConversation}
              onClose={handleCloseChat}
              currentAdmin={currentAdmin}
            />
          ) : (
            <div className="no-chat-selected">
              <FiMessageSquare size={64} />
              <h3>Select a conversation</h3>
              <p>Choose a conversation from the list to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminChat;

